import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { EChartOption } from 'echarts'
import { AnalyticsFetchTopSourcesResponseItem } from 'src/services/__generated__/api'
import { ReactComponent as LoudspeakerSvg } from 'src/assets/svg/loudspeaker.svg'
import { getNumberOrZero } from 'src/utils/getNumberOrZero'
import { getPercentage } from 'src/utils/getPercentage'
import { COLORS } from 'src/constants/colors'
import en from 'src/constants/en'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { capitalizeFirstLetter } from 'src/utils/capitalizeFirstLetter'
import { numberWithCommas } from 'src/utils/numberFormat'
import styles from './topSourcesChart.module.scss'
import { ChartInfo } from './interfaces/chartInfo.interface'
import { ChartDataItem } from './interfaces/chartData.interface'
import { EChart } from '../../EChart'
import { calculateToTalMentions } from '../utils/calculateToTalMentions'

interface TopSourcesChartProps {
  mention: AnalyticsFetchTopSourcesResponseItem
  overallMentions: number
}

const CHART_SIZE = 164

export const TopSourcesChart: FC<TopSourcesChartProps> = ({
  mention,
  overallMentions,
}) => {
  const chartData: ChartDataItem[] = [
    {
      name: en.positive,
      percentage: getNumberOrZero(mention?.positivePercentage),
      value: getNumberOrZero(mention?.positives),
    },
    {
      name: en.negative,
      percentage: getNumberOrZero(mention?.negativePercentage),
      value: getNumberOrZero(mention?.negatives),
    },
    {
      name: en.neutral,
      percentage: getNumberOrZero(mention?.neutralPercentage),
      value: getNumberOrZero(mention?.neutrals),
    },
  ]

  const maxDataItem = chartData?.reduce((prev, current) => {
    return prev.value > current.value ? prev : current
  })

  const totalMentions = calculateToTalMentions(mention)

  const [chartInfo, setChartInfo] = useState<ChartInfo>({
    percentage: maxDataItem.percentage,
    value: maxDataItem.value,
  })

  const chartContainerRef = useRef<HTMLDivElement>(null)

  const options = useMemo(() => {
    if (!mention) {
      return undefined
    }

    const options: EChartOption<EChartOption.SeriesPie> = {
      backgroundColor: 'rgba(0,0,0,0)',
      color: [COLORS.malibu, COLORS.burningOrange, COLORS.grey200],
      series: [
        {
          data: chartData,
          emphasis: {
            // @ts-ignore-next-line
            scale: false,
          },
          height: CHART_SIZE,
          hoverAnimation: false,
          itemStyle: {
            borderColor: COLORS.white,
            borderWidth: 7,
          },
          label: {
            show: false,
          },
          labelLine: {
            normal: {
              show: false,
            },
          },
          left: 'center',
          radius: [61, 80],
          type: 'pie',
          width: CHART_SIZE,
        },
      ],
    }

    return options
  }, [mention])

  // After chart renders, clear the white background ECharts sets inline on canvas
  useEffect(() => {
    const timer = setTimeout(() => {
      const canvas = chartContainerRef.current?.querySelector('canvas')
      if (canvas) canvas.style.background = 'transparent'
      const wrapper =
        chartContainerRef.current?.querySelector<HTMLDivElement>('div')
      if (wrapper) wrapper.style.background = 'transparent'
    }, 50)
    return () => clearTimeout(timer)
  }, [options])

  return (
    <div className={styles.card}>
      {/* Donut chart */}
      <div className={styles.chart} ref={chartContainerRef}>
        <div className={styles.chartInfo}>
          <div className={styles.chartTitle}>
            {chartInfo.percentage.toFixed(0)}%
          </div>
          <div className={styles.chartSubTitle}>
            <span>{numberWithCommas(chartInfo.value)}</span>
            <LoudspeakerSvg className={styles.loudspeakerIcon} />
          </div>
        </div>

        {options && (
          <div style={{ background: 'transparent' }}>
            <EChart<ChartDataItem>
              height={164}
              onMouseOut={() => {
                setChartInfo({
                  percentage: (maxDataItem.value / totalMentions) * 100,
                  value: maxDataItem.value,
                })
              }}
              onMouseOver={(event) => {
                setChartInfo({
                  percentage: event?.data.percentage,
                  value: event?.data.value,
                })
              }}
              options={options}
              width={164}
            />
          </div>
        )}
      </div>

      {/* Platform name */}
      <div className={styles.name}>
        {mention?.name &&
          platformDisplayLabel(
            mention.name,
            capitalizeFirstLetter(mention.name)
          )}
      </div>

      {/* Bottom stats — centered */}
      <div className={styles.bottomStats}>
        <div className={styles.statsRow}>
          <span className={styles.statPercentage}>
            {getPercentage(
              getNumberOrZero(totalMentions),
              getNumberOrZero(overallMentions)
            )}
          </span>
          <span className={styles.statDivider} />
          <span className={styles.statCount}>
            {numberWithCommas(getNumberOrZero(totalMentions))}
            <LoudspeakerSvg className={styles.statLoudspeaker} />
          </span>
        </div>
        <span className={styles.statLabel}>Of Total Mentions</span>
      </div>
    </div>
  )
}
